import React from "react";
import {
  formatName,
  get_t_str,
  get_t_str_minute_precision,
} from "../../../../../helpers/helpers";
import { formatDateReadableWithDayOfWeekNoYear } from "../../../requests/utilities/helpers";
import { roleColorHex, shiftRoleId } from "./roleColors";

const DOW = [0, 1, 2, 3, 4, 5, 6];

// Time-of-day accents are a thin left stripe, not a cell fill: readable in
// color, invisible in cost. Kept saturated enough to survive a grayscale
// printer as three distinct tones.
export const TONES = {
  morning: { color: "#f59e0b", label: "Morning" },
  afternoon: { color: "#f43f5e", label: "Afternoon" },
  evening: { color: "#3b82f6", label: "Evening" },
};

export function shiftTone(shift) {
  const start = shift.shift_start;
  const end = shift.shift_end;
  if (start <= 10) return "morning";
  if (start < 16 && end < 21) return "afternoon";
  return "evening";
}

// items: [{label, color}]
export function StripeLegend({ items }) {
  if (!items?.length) return null;
  return (
    <div className="flex flex-wrap justify-end gap-x-3 gap-y-0.5 max-w-[60%]">
      {items.map((t) => (
        <div key={t.label} className="flex items-center gap-1.5">
          <span
            className="print-accent inline-block w-[3px] h-3 rounded-sm"
            style={{ backgroundColor: t.color }}
          />
          <span className="text-[10px] text-slate-500">{t.label}</span>
        </div>
      ))}
    </div>
  );
}

export const COLOR_MODES = [
  { value: "none", label: "None" },
  { value: "time", label: "Time of day" },
  { value: "role", label: "Role" },
];

export function getBreakLines(shift) {
  const lines = [];
  if (shift.break_length > 0 && shift.break_start != null) {
    lines.push(
      `B ${get_t_str_minute_precision(shift.break_start)}–${get_t_str_minute_precision(
        shift.break_start + shift.break_length
      )}`
    );
  }
  if ((shift.break_length_2 || 0) > 0 && shift.break_start_2 != null) {
    lines.push(
      `B2 ${get_t_str_minute_precision(shift.break_start_2)}–${get_t_str_minute_precision(
        shift.break_start_2 + shift.break_length_2
      )}`
    );
  }
  (shift.paid_breaks || []).forEach((pb) => {
    lines.push(
      `PB ${get_t_str_minute_precision(pb.break_start)}–${get_t_str_minute_precision(
        pb.break_start + pb.break_duration
      )}`
    );
  });
  return lines;
}

export function shiftTimeLabel(shift) {
  return `${get_t_str(shift.shift_start)}–${
    shift.business_decline ? "BD" : get_t_str(shift.shift_end)
  }`;
}

function availabilityText(availList = [], timeoffList = []) {
  if (timeoffList.length > 0) return "Time off";
  if (
    availList.length > 0 &&
    availList[0].available_from === 0 &&
    availList[0].available_to === 0
  ) {
    return "Unavailable";
  }
  return "";
}

function ShiftEntry({ shift, colorMode, roleNames, showBreaks }) {
  let color = null;
  if (colorMode === "time") color = TONES[shiftTone(shift)].color;
  if (colorMode === "role") color = roleColorHex(shiftRoleId(shift), roleNames);
  return (
    <div
      className={`print-accent py-px ${color ? "pl-1.5" : ""}`}
      style={color ? { borderLeft: `3px solid ${color}` } : undefined}
    >
      <p className="font-semibold leading-tight whitespace-nowrap">
        {shiftTimeLabel(shift)}
      </p>
      {shift.station?.name && (
        <p className="text-slate-500 leading-tight">{shift.station.name}</p>
      )}
      {showBreaks &&
        getBreakLines(shift).map((line, i) => (
          <p
            key={i}
            className="text-slate-400 italic leading-tight text-[9px]"
          >
            {line}
          </p>
        ))}
    </div>
  );
}

function SheetNotes({ notes }) {
  if (!notes?.length) return null;
  return (
    <div className="mt-5 print-avoid-break">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-300 pb-1 mb-1.5">
        Notes
      </p>
      <div className="grid grid-cols-2 gap-x-8 gap-y-1">
        {notes.map((note, i) => (
          <div
            key={i}
            className="text-[10px] leading-snug flex gap-2 py-0.5 border-b border-slate-100"
          >
            <span className="shrink-0 w-[88px] text-slate-500">
              {formatDateReadableWithDayOfWeekNoYear(note.date)}
              {note.start_time && (
                <span className="block text-[9px]">
                  {get_t_str(note.start_time)}
                  {note.end_time && ` – ${get_t_str(note.end_time)}`}
                </span>
              )}
            </span>
            <span className="min-w-0">
              <span className="font-semibold text-slate-900">{note.title}</span>
              {note.description && (
                <span className="block text-slate-500">{note.description}</span>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * One printable weekly schedule: header, table, optional notes.
 *
 * props
 *  title / subtitle        store name and week range
 *  dayNames                7 column headers
 *  employees               rows shaped like store_team_member_weekly_info
 *  openShiftsByDay         [[shift], ...] length 7 (already role-filtered)
 *  subtractOtherStoreHours basic mode's rows carry company-wide `hours`
 *  notes
 *  roleNames               role catalogue, for role colors
 *  options { showHours, showTotals, colorMode ("none"|"time"|"role"),
 *            showUnavailability, showBreaks, showOpenShifts, showNotes,
 *            showRole }
 */
export default function WeeklyScheduleSheet({
  title,
  subtitle,
  dayNames,
  employees = [],
  openShiftsByDay,
  subtractOtherStoreHours = false,
  notes,
  roleNames,
  options,
}) {
  const {
    showHours = true,
    showTotals = true,
    colorMode = "time",
    showUnavailability = true,
    showBreaks = false,
    showOpenShifts = true,
    showNotes = true,
    showRole = true,
  } = options || {};

  const openByDay = DOW.map((dow) => openShiftsByDay?.[dow] || []);
  const openHoursByDay = openByDay.map((day) =>
    day.reduce((sum, s) => sum + (s.hours || 0), 0)
  );
  const totalOpenHours = openHoursByDay.reduce((a, b) => a + b, 0);
  const openShiftsVisible = showOpenShifts && totalOpenHours > 0;

  const employeeHours = (e) =>
    subtractOtherStoreHours ? e.hours - (e.other_store_hours || 0) : e.hours;

  const hoursByDay = DOW.map(
    (dow) =>
      employees.reduce(
        (sum, e) => sum + (e[dow] || []).reduce((s, sh) => s + sh.hours, 0),
        0
      ) + (openShiftsVisible ? openHoursByDay[dow] : 0)
  );
  const totalHours =
    employees.reduce((sum, e) => sum + employeeHours(e), 0) +
    (openShiftsVisible ? totalOpenHours : 0);

  const columnCount = 1 + (showHours ? 1 : 0) + 7;

  let legend = null;
  if (colorMode === "time") {
    legend = Object.values(TONES);
  } else if (colorMode === "role") {
    const ids = new Set();
    employees.forEach((e) => DOW.forEach((d) => (e[d] || []).forEach((s) => ids.add(shiftRoleId(s)))));
    if (openShiftsVisible) openByDay.forEach((day) => day.forEach((s) => ids.add(shiftRoleId(s))));
    legend = (roleNames || [])
      .filter((r) => ids.has(r.id))
      .map((r) => ({ label: r.role_name, color: roleColorHex(r.id, roleNames) }));
  }
  const dayCell = "align-middle py-1 px-1.5 border-l border-slate-200";
  const labelCell = "align-middle py-1 pr-2";

  return (
    <div>
      <div className="flex items-end justify-between pb-2 mb-1 border-b-2 border-slate-900">
        <div>
          <h3 className="text-base font-semibold leading-tight">{title}</h3>
          {subtitle && (
            <p className="text-[11px] text-slate-500 leading-tight mt-0.5">
              {subtitle}
            </p>
          )}
        </div>
        <StripeLegend items={legend} />
      </div>

      <table className="w-full table-fixed border-collapse text-[10px] leading-tight">
        <colgroup>
          <col style={{ width: 150 }} />
          {showHours && <col style={{ width: 52 }} />}
          {DOW.map((d) => (
            <col key={d} />
          ))}
        </colgroup>
        <thead>
          <tr className="border-b border-slate-400 text-[9px] uppercase tracking-wide text-slate-500">
            <th scope="col" className="text-left font-semibold py-1.5 pr-2">
              Team member
            </th>
            {showHours && (
              <th scope="col" className="text-right font-semibold py-1.5 px-1.5">
                Hrs
              </th>
            )}
            {dayNames.map((d) => (
              <th
                key={d}
                scope="col"
                className="text-left font-semibold py-1.5 px-1.5 border-l border-slate-200"
              >
                {d}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {employees.length === 0 && (
            <tr>
              <td
                colSpan={columnCount}
                className="py-6 text-center text-slate-400 italic"
              >
                No team members match these settings.
              </td>
            </tr>
          )}
          {employees.map((e) => (
            <tr
              key={e.store_team_member_id ?? e.id ?? e.employee_id}
              className="border-b border-slate-200"
            >
              <th scope="row" className={`${labelCell} text-left font-normal`}>
                <p className="font-semibold text-[10.5px] leading-tight">
                  {formatName(`${e.first_name} ${e.last_name}`)}
                </p>
                {showRole && e.role && (
                  <p className="text-slate-500 text-[9px] leading-tight">
                    {e.role}
                  </p>
                )}
              </th>
              {showHours && (
                <td className="align-middle py-1 px-1.5 text-right tabular-nums text-slate-700">
                  {employeeHours(e).toFixed(2)}
                </td>
              )}
              {DOW.map((dow) => {
                const shifts = e[dow] || [];
                if (shifts.length === 0) {
                  const text = showUnavailability
                    ? availabilityText(e[`${dow}_availability`], e[`${dow}_timeoff`])
                    : "";
                  return (
                    <td key={dow} className={dayCell}>
                      {text && (
                        <p className="text-slate-400 italic text-[9px]">{text}</p>
                      )}
                    </td>
                  );
                }
                return (
                  <td key={dow} className={dayCell}>
                    <div className="space-y-1">
                      {shifts.map((shift, i) => (
                        <ShiftEntry
                          key={shift.id ?? i}
                          shift={shift}
                          colorMode={colorMode}
                          roleNames={roleNames}
                          showBreaks={showBreaks}
                        />
                      ))}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
        {(openShiftsVisible || showTotals) && (
          <tfoot>
            {openShiftsVisible && (
              <tr className="border-b border-slate-200">
                <th scope="row" className={`${labelCell} text-left`}>
                  <p className="font-semibold text-[10.5px]">Open shifts</p>
                  <p className="text-slate-500 text-[9px]">Unassigned</p>
                </th>
                {showHours && (
                  <td className="align-middle py-1 px-1.5 text-right tabular-nums text-slate-700">
                    {totalOpenHours.toFixed(2)}
                  </td>
                )}
                {openByDay.map((day, dow) => (
                  <td key={dow} className={dayCell}>
                    <div className="space-y-1">
                      {day.map((shift, i) => (
                        <ShiftEntry
                          key={shift.id ?? i}
                          shift={shift}
                          colorMode={colorMode}
                          roleNames={roleNames}
                          showBreaks={showBreaks}
                        />
                      ))}
                    </div>
                  </td>
                ))}
              </tr>
            )}
            {showTotals && (
              <tr className="border-t-2 border-slate-900 font-semibold">
                <th scope="row" className="text-left py-1.5 pr-2">
                  Total hours
                  {/* Without the per-person column the weekly total still
                      needs a home, so it rides along in the label. */}
                  {!showHours && (
                    <span className="ml-1.5 tabular-nums">{totalHours.toFixed(2)}</span>
                  )}
                </th>
                {showHours && (
                  <td className="py-1.5 px-1.5 text-right tabular-nums">
                    {totalHours.toFixed(2)}
                  </td>
                )}
                {hoursByDay.map((h, dow) => (
                  <td
                    key={dow}
                    className="py-1.5 px-1.5 tabular-nums border-l border-slate-200"
                  >
                    {h.toFixed(2)}
                  </td>
                ))}
              </tr>
            )}
          </tfoot>
        )}
      </table>

      {showBreaks && (
        <p className="mt-1.5 text-[9px] text-slate-400">
          B = break · B2 = second break · PB = paid break · BD = business decline
        </p>
      )}

      {showNotes && <SheetNotes notes={notes} />}
    </div>
  );
}
